/**
 * Per-engine marketing content records (A7Y-5448).
 *
 * Each engine page at `/engines/{slug}` answers four questions, in this order:
 * what the engine reads, what it scores, what it recommends, and what it
 * cannot see. The answers come from the engine code in
 * `a7y-functions/src/aso-engines/**` (and, for Competitors,
 * `a7y-functions/src/contexts/competitor-intelligence/`), not from the
 * messaging framework. Which plans include an engine is not written here at
 * all: `lib/engine-plans.ts` computes it from `PLAN_TIERS`.
 *
 * Every figure in these strings is registered in the public-claim registry
 * (`packages/platform-health/src/public-claim-registry-data/marketing-feature-claims.ts`).
 * A new figure gets a registry entry with its derivation in the same change.
 */

import type { EngineSlug } from './engine-plans';

export interface EngineLink {
  /** Display label for the link. */
  name: string;
  /** Engine slug (the `{slug}` in `/engines/{slug}`). */
  slug: EngineSlug;
}

export interface EngineContent {
  /** Slug for the page URL. `/engines/{slug}`. */
  slug: EngineSlug;
  /** Engine name, as the product shows it. */
  name: string;
  /** One sentence: what the engine does. Also the page's SEO description and its llms.txt line. */
  summary: string;
  /** The inputs the engine consumes, and from where. */
  reads: string[];
  /** What it measures: one lead sentence, then the parts. */
  scores: { summary: string; items: string[] };
  /** The kinds of rows or findings it produces. */
  recommends: string[];
  /** What is outside its view: data it has no access to, cases it skips. */
  cannotSee: string[];
  /** Engines whose output this one uses or feeds. */
  related: EngineLink[];
}

// =============================================================================
// AI Discoverability — a7y-functions/src/aso-engines/ai-discovery-engine/
// =============================================================================

export const AI_DISCOVERABILITY: EngineContent = {
  slug: 'ai-discoverability',
  name: 'AI Discoverability',
  summary:
    'Asks AI assistants the questions people ask when looking for an app like this one, and measures whether the app is named, where, and how.',
  reads: [
    "The listing's title, subtitle or short description, description and category.",
    "A battery of 20 prompts written for the app's category, use case and audience. A manual audit asks the same 20 until the project swaps or refreshes them; a scheduled re-audit asks 10 prompts from the set.",
    'Answers from up to five assistants: ChatGPT, Claude, Gemini, Perplexity, and Google AI Overviews. The plan sets which assistants are queried.',
    'Google AI Overviews are read from Google results pages.',
    "Search phrases around the category from Google and YouTube autocomplete, Google's related searches and People Also Ask, store autocomplete, Google Ads keyword ideas, Google Trends and Reddit. On Google Play, the app's own Play Console search terms when the account is connected.",
    "The app's website and publisher pages when they are known, and the competitor set.",
  ],
  scores: {
    summary:
      'A 0 to 100 score over these measures; community health counts only when there is community data. Position and sentiment carry less weight when the app is named only a few times.',
    items: [
      'Presence: how often answers name the app.',
      'Position: where in an answer the app is named. First place scores highest.',
      'Sentiment: how answers describe the app.',
      "Intent coverage: which of the app's user intents the answers connect to it. The intents come from the User Intent engine; on plans without it there are none to check, and this part is held at a neutral middle value rather than scored.",
      "Competitive standing: the app's share of mentions against its competitors.",
      'Community health: Reddit discussion of the app, when there is any.',
    ],
  },
  recommends: [
    'Fixes for weak measures: the app is missing from answers, named low in them, absent from an intent (only where User Intent ran), or cited mostly from one kind of source.',
    'Search phrases to work into the subtitle on the App Store, or the short or full description on Google Play, naming the field and the phrase. These fields are indexed for store search and read by AI assistants.',
    'No new phrases for the title. The stores index the title as well: the title, subtitle and keyword field on the App Store, the title, short and full description on Google Play. A phrase already in the title counts as present, and title edits come from the Store Text engine.',
    'No phrases for the App Store description. Apple does not index it for search, so a phrase there does nothing for App Store ranking; for App Store search, the subtitle and keyword field are what count.',
    'Phrases and questions for the website and its FAQ, and pages that AI answers cite, where a mention of the app would count.',
  ],
  cannotSee: [
    'The App Store keyword field. Listing recommendations target the subtitle, and on Google Play the short and full description.',
    'Whether an answer led to an install.',
    'Draft listings before launch: the engine does not run on them.',
    'Search terms that led to installs on the App Store. App Store Connect does not report them; Google Play Console does, and the engine reads them when the account is connected.',
    'A stable answer. Assistants answer differently from run to run, so one audit is a sample.',
  ],
  related: [
    { name: 'User Intent', slug: 'user-intent' },
    { name: 'Competitors', slug: 'competitors' },
    { name: 'Store Text', slug: 'store-text' },
  ],
};

// =============================================================================
// Keywords & Semantic Clusters — a7y-functions/src/aso-engines/keyword-engine/,
// a7y-functions/src/backend-services/keyword-service/
// =============================================================================

export const KEYWORDS_AND_CLUSTERS: EngineContent = {
  slug: 'keywords-and-semantic-clusters',
  name: 'Keywords & Semantic Clusters',
  summary:
    'Finds the search terms an app ranks for and the terms it could target, with search volume, difficulty and current rank for each.',
  reads: [
    'The title, subtitle or short description, and description of the app and of up to 10 competitors.',
    'The App Store keyword field, when App Store Connect is connected or the field is entered by hand.',
    "Search volume on both stores, estimated from Google web-search demand in Google Ads. On the App Store the estimate is shown on a popularity-style scale, but it is not Apple's own popularity value: compare it with other terms in the same audit, not with Apple Ads or other tools' popularity figures.",
    'Difficulty from store search results, reviews and Google Ads cost per click; current rank from live store search.',
    'Suggested terms from Apple Ads or Google Ads keyword ideas and from store autocomplete.',
  ],
  scores: {
    summary: 'A 0 to 100 score from these readings:',
    items: [
      'Ranking strength: how many of the terms the app ranks for are near the top of search.',
      'Opportunity: volume against difficulty for each suggested term, raised when the term is trending up.',
      "Top-term rank: the app's rank for the market's top search terms.",
      "Listing coverage: whether the market's top terms appear in the title, the subtitle or short description, the description, or the App Store keyword field.",
    ],
  },
  recommends: [
    'A suggested term to add to a named field, when its opportunity is high.',
    'A term the app already ranks for whose rank can improve.',
    "Each row names the field and the change, with the term's current rank or its absence from search.",
    'Terms grouped into themes, each marked strong, moderate, weak or opportunity from its rank, search volume, difficulty and trend, and whether it appears in the listing.',
  ],
  cannotSee: [
    "Apple Ads search popularity, Apple's own figure.",
    "Google Play's own search volume. Volume on both stores is a Google web-search figure, so it is a proxy for store search.",
    'Text inside screenshots.',
    'Which App Store field a covered term sits in. A top term found only in the App Store description counts as covered, although Apple does not index the description for search.',
    "More than 300 suggested keywords in one audit; the cap is lower when the audit runs short of time. The app's own terms are measured separately, under a limit of their own.",
    'Long-tail terms in small categories, where search data is sparse.',
  ],
  related: [
    { name: 'Store Text', slug: 'store-text' },
    { name: 'User Intent', slug: 'user-intent' },
    { name: 'Competitors', slug: 'competitors' },
  ],
};

// =============================================================================
// User Intent — a7y-functions/src/aso-engines/intent-engine/
// =============================================================================

export const USER_INTENT: EngineContent = {
  slug: 'user-intent',
  name: 'User Intent',
  summary:
    'Lists the needs that lead people to an app like this one, and checks which of them the listing states, and how clearly.',
  reads: [
    'The title, subtitle or short description, description and category.',
    "The top competitors' titles, subtitles and descriptions.",
    'Review themes from the User Sentiment engine.',
    'The search phrases gathered for AI Discoverability, when that step ran.',
  ],
  scores: {
    summary: 'A 0 to 100 score over these measures:',
    items: [
      'Breadth: how many of the relevant intents the listing covers, weighted by search demand where it is known.',
      'Depth: how strongly the listing states the intents it does state.',
      'Gap impact: the weight of the intents the listing misses. Intents found in reviews and search phrases count most.',
      'Differentiation: intents the listing covers that the competitors do not.',
    ],
  },
  recommends: [
    "A statement to add for the most relevant intents the listing misses or states weakly, naming the field: the title, the subtitle (short description on Google Play), the description (full description on Google Play), promotional text (an App Store field) or What's New. What's New is named only for a newly shipped capability, a message that lasts one release.",
    'Rows that would make a claim the app must be able to support ask for confirmation first.',
    'Regulated claims, such as therapy or clinical benefit, are not recommended outside the Medical category.',
  ],
  cannotSee: [
    'Review themes, when User Sentiment did not run.',
    'Search demand for a draft listing before launch.',
    'Which store has promotional text. The engine does not check, so a row for a Google Play listing can still name it.',
    "Search terms that led to installs on the App Store. App Store Connect does not report them; Google Play Console does, and the engine reads them when the account is connected. Demand is otherwise estimated from public search, community and social data, and Google Ads keyword ideas.",
  ],
  related: [
    { name: 'Keywords & Semantic Clusters', slug: 'keywords-and-semantic-clusters' },
    { name: 'User Sentiment', slug: 'sentiment' },
    { name: 'AI Discoverability', slug: 'ai-discoverability' },
  ],
};

// =============================================================================
// Store Text — a7y-functions/src/aso-engines/storetext-engine/
// =============================================================================

export const STORE_TEXT: EngineContent = {
  slug: 'store-text',
  name: 'Store Text',
  summary:
    'Scores the title, subtitle or short description, and description together for space used, term placement and writing quality, and flags claims the text cannot support.',
  reads: [
    'The title and subtitle on the App Store, the title and short description on Google Play, and the description on both.',
    'The App Store keyword field, when App Store Connect is connected or the field is entered by hand.',
    "Keyword data from the Keywords engine and the competitors' titles.",
    "The app's live rating, review count and last update date, to check claims made in the text.",
  ],
  scores: {
    summary: 'A 0 to 100 score from these parts. Google Play has no cross-field part.',
    items: [
      "Character use: how much of each field's character limit is used.",
      'Field optimization: whether each field holds the terms suited to it.',
      'Content quality: clarity and persuasion, field by field.',
      'Cross-field coherence, App Store only: how the title, subtitle and keyword field divide the terms between them, lowered for words repeated across them, which index once.',
    ],
  },
  recommends: [
    'Field edits, each with the current text and the proposed text.',
    'Claims to remove or rewrite: superlatives with no support, out-of-date freshness claims, rating claims the store data contradicts, and claims with policy or trademark risk.',
    'Fields written in a language other than the storefront language.',
  ],
  cannotSee: [
    "Promotional text and What's New.",
    'A cross-field score part on Google Play, which has no subtitle or keyword field.',
    'The App Store keyword field, unless App Store Connect is connected or the field is entered by hand.',
    'Installs, which are not part of the score. Store Analytics shows store traffic and installs before and after a change.',
  ],
  related: [
    { name: 'Keywords & Semantic Clusters', slug: 'keywords-and-semantic-clusters' },
    { name: 'Policy Checker', slug: 'policy-checker' },
    { name: 'Localization Analysis', slug: 'localization-analysis' },
  ],
};

// =============================================================================
// Screenshots — a7y-functions/src/aso-engines/screenshot-engine/
// =============================================================================

export const SCREENSHOTS: EngineContent = {
  slug: 'screenshots',
  name: 'Screenshots',
  summary:
    'Reads each phone screenshot with a vision model and scores the set for clarity, coverage, consistency and fit with the listing text.',
  reads: [
    'The phone screenshots on the store page.',
    'The caption text in each image, read by the vision model and tagged as marketing, interface or branding text.',
    "The listing text, the storefront language, and the app's rating and review count.",
    "How often the competitors' screenshots carry particular elements, such as a rating or an award.",
  ],
  scores: {
    summary:
      'A 0 to 100 score over these measures. Friction reducers and variety count on their own and again inside coverage. The weakest measure other than friction reducers caps the total, and the cap is lower when no caption is in the storefront language.',
    items: [
      "Clarity: whether each frame's message reads at a glance.",
      'Friction reducers: whether the set shows an outcome, the solution and proof.',
      'Variety: how many different messages the frames carry, rather than repeating one.',
      'Coverage: how many use cases, scenarios and audiences the set shows, combined with friction reducers and variety.',
      'Visual cohesion across the set.',
      'Fit between the captions and the listing text.',
    ],
  },
  recommends: [
    'Changes to a named frame, with its score on the measure that is weak.',
    'Captions in the storefront language for the opening frames, when they are written in another language.',
    'A preview video, whenever the store page has none. The engine does not check whether the leading listings in the category use one, so treat it as something to test, with Product Page Optimization on the App Store or a store listing experiment on Google Play, before keeping it.',
    "Elements from a fixed checklist, such as a rating or an award, that competitors' screenshots carry and these do not.",
  ],
  cannotSee: [
    'Tablet screenshots.',
    'The Google Play feature graphic.',
    "The preview video's content.",
    "Whether a video lifts conversion in the app's category. The video row is not weighed against competitors' use of video.",
    'Installs. The score rates the images, not what visitors did.',
  ],
  related: [
    { name: 'Icon', slug: 'icon' },
    { name: 'Store Text', slug: 'store-text' },
    { name: 'Localization Analysis', slug: 'localization-analysis' },
  ],
};

// =============================================================================
// Icon — a7y-functions/src/aso-engines/icon-engine/
// =============================================================================

export const ICON: EngineContent = {
  slug: 'icon',
  name: 'Icon',
  summary:
    'Scores the app icon for what it depicts, its contrast and colour, how it reads at small sizes, and how well it matches the listing and screenshots.',
  reads: [
    'The icon image.',
    'The title, subtitle and description.',
    'The opening phone screenshots, to compare style.',
  ],
  scores: {
    summary: 'A 0 to 100 score, the weighted mean of these measures:',
    items: [
      'Functional fit: whether the image matches what the app does.',
      'Consistency with the listing text.',
      'Contrast, graded against the WCAG levels.',
      'Colour harmony.',
      'Match with the screenshots.',
      'Issues found, weighted by severity.',
      "Clarity at small sizes, estimated from the full-size image's visual complexity.",
    ],
  },
  recommends: [
    'One change per audit: the one with the most impact, naming what it addresses, such as brand clarity, consistency with the listing or colour.',
    'A household-name brand, judged from its installs or review count, is told how to execute its mark better, such as contrast or legibility at small sizes, and never to add imagery or change its colours or form.',
  ],
  cannotSee: [
    "Competitor icons. The audit does not compare the icon with other apps' icons.",
    'Test results. No A/B or store-experiment data is read.',
    'Culturally specific symbols.',
  ],
  related: [
    { name: 'Screenshots', slug: 'screenshots' },
    { name: 'Store Text', slug: 'store-text' },
  ],
};

// =============================================================================
// User Sentiment — a7y-functions/src/aso-engines/sentiment-engine/
// =============================================================================

export const SENTIMENT: EngineContent = {
  slug: 'sentiment',
  name: 'User Sentiment',
  summary:
    'Reads recent reviews from the audited storefront and reports what users praise, what they complain about, and which complaints the listing can address.',
  reads: [
    "The newest 300 reviews from the storefront inside the plan's review window. The cap is the same on every plan, so a longer window reaches older reviews only for an app that does not fill the cap inside a shorter one.",
    "Each review's rating and text, and the public rating with its star distribution.",
    'On the App Store, reviews from App Store Connect when it is connected, otherwise the public review feed.',
  ],
  scores: {
    summary:
      'A 0 to 100 score from these parts. The reviews also get a health grade, from best to worst excellent, good, needs attention, poor or critical, and the score never sits above the range of its grade. A severe risk holds the grade at needs attention or lower. Risk severity counts only when there are main complaints, and user impact only when red flags were counted against the reviews:',
    items: [
      'Sentiment health: the star rating and the share of positive, neutral and negative reviews, adjusted for the rating trend.',
      'Risk severity: how serious the main complaints are. A main complaint is one of the few issues with the most impact across the reviews, whatever its kind.',
      'User impact: the share of reviews each red flag reaches. A red flag is a critical problem raised in reviews, such as crashes, failed logins, payment trouble or refund demands.',
    ],
  },
  recommends: [
    'Risks, red flags and priority actions. Risks and red flags carry the number of sampled reviews that mention them, when it was measured.',
    'Recommendations stay inside what the listing owner controls, chiefly listing changes and developer replies. A complaint the listing cannot address is labelled a product issue, with a short note for the team, and gets no engineering or release advice.',
  ],
  cannotSee: [
    'Reviews from other storefronts.',
    "Competitors' reviews. No comparison with them runs.",
    'Apps with fewer than 25 reviews inside the window. The engine does not run.',
    "Reviews older than the plan's window.",
  ],
  related: [
    { name: 'User Intent', slug: 'user-intent' },
    { name: 'Store Text', slug: 'store-text' },
  ],
};

// =============================================================================
// Competitors — a7y-functions/src/contexts/competitor-intelligence/competitor-discovery-service/
// =============================================================================

export const COMPETITORS: EngineContent = {
  slug: 'competitors',
  name: 'Competitors',
  summary:
    'Picks the apps the audit compares against, from store search and charts, and scores whether that set is a usable benchmark.',
  reads: [
    "Store search results for the app's keywords, the store's similar-apps list, and category charts.",
    'Public listing data for each candidate: rating, review count, last update, publisher, and installs where the store shows them.',
    'Competitors the project tracks by hand. They take seats in the set first; tracked competitors beyond its seats are still tracked, but are not used in the audit benchmark.',
    'Rivals found when the same app was audited on the other store.',
  ],
  scores: {
    summary:
      "A 0 to 100 score of the set itself, which holds up to 10 competitors. On Google Play, and in several App Store categories including games, a language model rates each candidate as a direct rival (same core use and audience), an adjacent app, a same-genre app or unrelated, and direct rivals rank first. Apps whose names borrow a better-known rival's are dropped; in games, only when they are small beside the audited app. The publisher's own other apps are dropped too, except in games, where one of its titles is kept when it is rated a direct rival; for an App Store game filed under a non-game primary genre, only when its title or subtitle also names a subject of the audited app's title or subtitle. Stale and low-rated apps are mostly dropped; large established rivals and competitors tracked by hand are kept.",
    items: [
      'Count: how many seats are filled.',
      "Quality: whether each competitor's installs, rating count and rating are known and strong.",
      'Breadth: the size of the set, discounted when it is a tiny share of the category.',
    ],
  },
  recommends: [
    'No recommendation rows of its own. The Keywords, User Intent and AI Discoverability engines compare against the set it picks.',
    'Notes on the set, such as too few competitors found, or rivals that average a very high rating, which means the app is benchmarked against strong listings.',
  ],
  cannotSee: [
    "Competitors' private data: console figures, revenue or conversion.",
    'Rivals that none of these sources surface. Add them to the project by hand.',
    'Discovery for a draft listing before launch. The set is the one the user names.',
  ],
  related: [
    { name: 'Keywords & Semantic Clusters', slug: 'keywords-and-semantic-clusters' },
    { name: 'AI Discoverability', slug: 'ai-discoverability' },
  ],
};

// =============================================================================
// Localization Analysis — a7y-functions/src/aso-engines/l11n-analysis-engine/
// =============================================================================

export const LOCALIZATION_ANALYSIS: EngineContent = {
  slug: 'localization-analysis',
  name: 'Localization Analysis',
  summary:
    'Checks, language by language, whether the store serves a localized listing or falls back to the default-language text.',
  reads: [
    "For a project, its own languages: on an App Store audit with App Store Connect connected, the localizations live there, including locales outside the inspected set such as English (U.K.); otherwise the project's primary and target languages, when it sets target languages. Languages outside that set are listed as languages the app could add, and are not inspected or scored.",
    "The public listing in each of 22 inspected languages, fetched from that language's home country, or from the audited storefront when the app is not sold there. Each language is read as one store locale: Spanish from Spain, Portuguese from Brazil, French from France. Chinese is read as both Simplified and Traditional. This is the set read when an audit has no project, or its project has neither App Store Connect connected nor target languages.",
    'The title, subtitle and description on the App Store; the title, short description and description on Google Play.',
    'Traffic by country from App Store Connect or Google Play Console, when connected.',
    'On the App Store, how the category charts split by country.',
    'Screenshot captions, for measurement units that do not suit a market.',
  ],
  scores: {
    summary: 'A 0 to 100 coverage score:',
    items: [
      'A field counts as localized when its text differs from the default-language text. The title weighs most.',
      'A title kept the same in every language, as a brand name usually is, is left out of the score rather than counted as a gap, provided other fields are localized.',
      "Google Play's automatic translations count for half.",
      'No score when the default-language listing, or every other language, could not be read.',
    ],
  },
  recommends: [
    "One row per language with a gap, ranked by the strongest evidence available: the app's own traffic from that country, then the category's chart share there, then a default order.",
    'A screenshot row when captions use units that do not suit the market.',
    "When the check read a project's own languages, a separate list of languages the app could add, ranked the same way. It is not scored.",
  ],
  cannotSee: [
    "Languages outside the set it reads. With neither App Store Connect connected nor target languages set, that is the inspected set above, whatever the plan.",
    'On the default inspected set, other locales of an inspected language, such as Spanish (Mexico), Portuguese (Portugal) or French (Canada); on any set, localizations a storefront indexes from another locale.',
    'Translation quality. A translated field counts whether the translation is good or not.',
    'The App Store keyword field, which is not public.',
    'Chart share by country for games.',
  ],
  related: [
    { name: 'Store Text', slug: 'store-text' },
    { name: 'Screenshots', slug: 'screenshots' },
  ],
};

// =============================================================================
// Policy Checker — a7y-functions/src/aso-engines/policy-checker/
// =============================================================================

export const POLICY_CHECKER: EngineContent = {
  slug: 'policy-checker',
  name: 'Policy Checker',
  summary:
    'Checks listing text that Apptonomy drafts, before it is published, for prohibited terms, misleading claims and competitor brand names.',
  reads: [
    "Drafts from Apptonomy's content generator, one field at a time: title, subtitle, keyword field, description and promotional text. On Google Play, the metadata edited in the listing builder.",
    "The platform, the field, the locale, the app's brand and the publisher's name.",
    "Rule lists kept by Apptonomy for each store: price and promotion words, platform names such as 'iPhone' or 'Google Play', filler words such as 'app' or 'new', and claim patterns such as '#1', 'best' or 'guaranteed'.",
    'A language-model pass for competitor brand names, which reads the context so an ordinary word is not flagged as a brand.',
  ],
  scores: {
    summary: 'No score. Each flag carries:',
    items: [
      'The term and the reason: competitor brand, prohibited term, misleading claim, or keyword quality.',
      'A level, prominent, standard or informational, that sets how strongly the flag is shown. Flags are listed most prominent first, and no level removes the term: the user decides.',
      "The likely consequence: rejection by the store, an edit to the app's keywords, action by a rights holder, or none.",
    ],
  },
  recommends: [
    "Flags on the draft. The draft's text is not changed and no term is removed automatically.",
  ],
  cannotSee: [
    'The live listing. The audit does not run this check; policy and trademark risks in the live text come from the Store Text engine.',
    "Screenshots, the icon, and the app's review history with Apple or Google.",
    "Store rules outside its lists. It is not a full reading of either store's guidelines.",
  ],
  related: [
    { name: 'Store Text', slug: 'store-text' },
    { name: 'Keywords & Semantic Clusters', slug: 'keywords-and-semantic-clusters' },
  ],
};

/** Registry of all engine content records, keyed by slug. */
export const ENGINE_CONTENT: Record<EngineSlug, EngineContent> = {
  'ai-discoverability': AI_DISCOVERABILITY,
  'keywords-and-semantic-clusters': KEYWORDS_AND_CLUSTERS,
  'user-intent': USER_INTENT,
  'store-text': STORE_TEXT,
  screenshots: SCREENSHOTS,
  icon: ICON,
  sentiment: SENTIMENT,
  competitors: COMPETITORS,
  'localization-analysis': LOCALIZATION_ANALYSIS,
  'policy-checker': POLICY_CHECKER,
};
